import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import MetricCard from './components/MetricCard';
import OccupancyHeatmap from './components/OccupancyHeatmap';
import ActivityFeed from './components/ActivityFeed';
import OccupancyTrendChart from './components/OccupancyTrendChart';
import FilterPanel from './components/FilterPanel';
import RefreshControl from './components/RefreshControl';
import CapacityAlert from './components/CapacityAlert';
import { useLanguage } from '../../contexts/LanguageContext';
import { BUILDINGS_CONFIG } from '../../contexts/BookingContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { supabase } from '../../lib/supabase';
import { getRoomOccupancyStatus, getTodayStr, generateValidationReport } from '../../utils/occupancyStatus';

// ─── Building capacity config ─────────────────────────────────────────────────
const ALL_BUILDINGS = Object.keys(BUILDINGS_CONFIG);
const ROOM_TYPES = ['Single', 'Double', 'Suite', 'Apartment'];

// Total rooms from config (authoritative source)
const CONFIG_TOTAL_ROOMS = Object.values(BUILDINGS_CONFIG)?.reduce(
  (sum, cfg) => sum + cfg?.floors?.reduce((s, f) => s + f?.rooms?.length, 0), 0
);

// ─── Derive heatmap data from stays + BUILDINGS_CONFIG ────────────────────────
function buildHeatmapData(stays, rooms, selectedBuildings) {
  const today = getTodayStr();
  const buildings = selectedBuildings?.length > 0 ? selectedBuildings : ALL_BUILDINGS;

  // Map full room number → resident name for occupied rooms
  const residentByRoom = {};
  stays?.forEach(s => {
    const rn = (s?.room_number || s?.room || '')?.trim();
    const checkIn  = s?.check_in_date  || s?.checkIn  || '';
    const checkOut = s?.check_out_date || s?.checkOut || '';
    const isOccupied = (s?.status === 'confirmed-stay' || s?.status === 'active') &&
                       today >= checkIn && today < checkOut;
    if (rn && isOccupied) residentByRoom[rn] = s?.resident_name || s?.residentName;
  });

  return buildings?.map(building => {
    const cfg = BUILDINGS_CONFIG?.[building];
    if (!cfg) return null;

    const floors = cfg?.floors?.map(floorCfg => {
      const rooms_list = floorCfg?.rooms?.map(fullRoomNumber => {
        const roomRecord = rooms?.find(r => (r?.room_number || r?.roomNumber) === fullRoomNumber);
        // Date-driven status
        const status = getRoomOccupancyStatus(fullRoomNumber, stays, roomRecord, today);

        // Display number: strip building prefix (e.g. "H-101" → "101")
        const parts = fullRoomNumber?.split('-');
        const displayNum = parts?.length >= 2 ? parts?.slice(1)?.join('-') : fullRoomNumber;

        return {
          roomNumber: displayNum,
          fullRoomNumber,
          status,
          type: building === 'L' ? 'Apartment' : floorCfg?.floor === 0 ? 'Studio' : 'Standard',
          occupancyRate: status === 'Occupied' ? 100 : 0,
          resident: residentByRoom?.[fullRoomNumber] || null,
        };
      });

      const occupied = rooms_list?.filter(r => r?.status === 'Occupied')?.length;
      return { floor: floorCfg?.floor, total: rooms_list?.length, occupied, rooms: rooms_list };
    });

    return { building, floors };
  })?.filter(Boolean);
}

// ─── Compute summary metrics ──────────────────────────────────────────────────
function computeMetrics(stays, rooms, heatmapData) {
  const today = getTodayStr();
  const totalRooms = heatmapData?.reduce(
    (sum, b) => sum + b?.floors?.reduce((s, f) => s + f?.total, 0), 0
  ) || CONFIG_TOTAL_ROOMS;

  const occupiedRooms = heatmapData?.reduce(
    (sum, b) => sum + b?.floors?.reduce((s, f) => s + f?.occupied, 0), 0
  );

  // Count reserved from heatmap (rooms with Reserved status)
  const reservedRooms = heatmapData?.reduce(
    (sum, b) => sum + b?.floors?.reduce(
      (s, f) => s + f?.rooms?.filter(r => r?.status === 'Reserved')?.length, 0
    ), 0
  );

  const availableRooms = Math.max(0, totalRooms - occupiedRooms - reservedRooms);
  const occupancyRate = totalRooms > 0 ? ((occupiedRooms / totalRooms) * 100)?.toFixed(1) : '0.0';

  const pendingCheckins = stays?.filter(s => {
    const ci = s?.check_in_date || s?.checkIn || '';
    return (s?.status === 'active-reservation' || s?.status === 'pending') && ci === today;
  })?.length;

  const activeResidents = occupiedRooms;

  const sevenDaysLater = new Date();
  sevenDaysLater?.setDate(sevenDaysLater?.getDate() + 7);
  const sevenDaysStr = sevenDaysLater?.toISOString()?.split('T')?.[0];
  const upcomingDepartures = stays?.filter(s => {
    const ci = s?.check_in_date  || s?.checkIn  || '';
    const co = s?.check_out_date || s?.checkOut || '';
    return (s?.status === 'confirmed-stay' || s?.status === 'active') &&
           today >= ci && today < co &&
           co >= today && co <= sevenDaysStr;
  })?.length;

  const alertBuildings = heatmapData?.filter(b => {
    const total = b?.floors?.reduce((s, f) => s + f?.total, 0);
    const occ = b?.floors?.reduce((s, f) => s + f?.occupied, 0);
    return total > 0 && (occ / total) >= 0.9;
  });

  return { totalRooms, occupiedRooms, reservedRooms, availableRooms, occupancyRate, pendingCheckins, activeResidents, upcomingDepartures, alertBuildings };
}

// ─── Build activity feed from stays ──────────────────────────────────────────
function buildActivityFeed(stays) {
  const sorted = [...stays]
    ?.filter(s => s?.createdAt || s?.checkIn)
    ?.sort((a, b) => {
      const ta = a?.createdAt ? new Date(a.createdAt) : new Date(a.checkIn);
      const tb = b?.createdAt ? new Date(b.createdAt) : new Date(b.checkIn);
      return tb - ta;
    })
    ?.slice(0, 12);

  return sorted?.map((s, idx) => {
    const isCompleted = s?.status === 'completed-stay' || s?.status === 'checked-out';
    const isConfirmed = s?.status === 'confirmed-stay' || s?.status === 'active';
    let type, title, description;
    if (isCompleted) {
      type = 'check-out';
      title = 'Completed Stay';
      description = `${s?.residentName} checked out from Room ${s?.room}`;
    } else if (isConfirmed) {
      type = 'check-in';
      title = 'Confirmed Stay';
      description = `${s?.residentName} checked into Room ${s?.room}`;
    } else {
      type = 'reservation';
      title = 'Active Reservation';
      description = `${s?.residentName} has an active reservation for Room ${s?.room}`;
    }
    return {
      id: s?.id || idx,
      type,
      title,
      description,
      location: `${s?.building}`,
      timestamp: s?.createdAt ? new Date(s.createdAt) : new Date(s.checkIn),
    };
  });
}

// ─── Build trend data (last 14 days based on stays) ──────────────────────────
function buildTrendData(stays) {
  const days = 14;
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d?.setDate(d?.getDate() - i);
    const dateStr = d?.toISOString()?.split('T')?.[0];
    const label = `${String(d?.getMonth() + 1)?.padStart(2, '0')}/${String(d?.getDate())?.padStart(2, '0')}`;
    const activeOnDay = stays?.filter(s => {
      const ci = s?.checkIn <= dateStr;
      const co = (s?.status === 'completed-stay' || s?.status === 'checked-out') ? s?.checkOut >= dateStr : true;
      return ci && co && (
        s?.status === 'confirmed-stay' ||
        s?.status === 'active-reservation' ||
        s?.status === 'completed-stay' ||
        s?.status === 'active' ||
        s?.status === 'pending' ||
        s?.status === 'checked-out'
      );
    })?.length;
    const rate = CONFIG_TOTAL_ROOMS > 0 ? Math.round((activeOnDay / CONFIG_TOTAL_ROOMS) * 100) : 0;
    result?.push({ date: label, occupancy: Math.min(rate, 100) });
  }
  return result;
}

// ─── Diagnostic: find a specific room's status across all data sources ────────
function diagnoseRoom(roomNumber, rawStays) {
  const matches = rawStays?.filter(s =>
    (s?.room_number || s?.room || '')?.trim() === roomNumber?.trim()
  );
  return {
    roomNumber,
    stayCount: matches?.length,
    stays: matches?.map(s => ({
      id: s?.id,
      status: s?.status,
      guestStatus: s?.guest_status || s?.guestStatus,
      residentName: s?.resident_name || s?.residentName,
      checkIn: s?.check_in_date || s?.checkIn,
      checkOut: s?.check_out_date || s?.checkOut,
      roomField: s?.room_number || s?.room,
    })),
    isOccupied: matches?.some(s => s?.status === 'confirmed-stay' || s?.status === 'active'),
    isReserved: matches?.some(s => s?.status === 'active-reservation' || s?.status === 'pending'),
    rootCause: matches?.length === 0
      ? 'No stay record found for this room number. Check that the room number in the stay matches BUILDINGS_CONFIG exactly (e.g. "H-101").'
      : matches?.every(s => s?.status === 'active-reservation' || s?.status === 'pending')
        ? 'Stay exists but status is "active-reservation" — check-in has NOT been performed yet. Use Check-in/Check-out → Check-in to convert to "confirmed-stay".'
        : matches?.some(s => s?.status === 'confirmed-stay')
          ? 'Stay is confirmed-stay — room SHOULD appear as Occupied. If heatmap still shows Reserved, verify room_number field exactly matches BUILDINGS_CONFIG format.'
          : `Stay status is "${matches?.[0]?.status}" — not a recognized occupied status.`,
  };
}

const OccupancyAnalytics = () => {
  const { t, language } = useLanguage();
  const realtime = useRealtime();

  // Normalize stays from Supabase — map DB column names to consistent field names
  const stays = React.useMemo(() => {
    return (realtime?.stays || [])?.map(s => ({
      ...s,
      residentName: s?.resident_name || s?.residentName || 'Unknown',
      // Normalize room: DB uses room_number, legacy uses room
      room: (s?.room_number || s?.room || '')?.trim(),
      checkIn: s?.check_in_date || s?.checkIn || '',
      checkOut: s?.check_out_date || s?.checkOut || '',
      guestStatus: s?.guest_status || s?.guestStatus,
      createdAt: s?.created_at,
    }));
  }, [realtime?.stays]);

  const rooms = realtime?.rooms || [];

  const [autoRefreshInterval, setAutoRefreshInterval] = useState(300000);
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [dateRange, setDateRange] = useState('month');
  const [alerts, setAlerts] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [isSyncing, setIsSyncing] = useState(false);
  const [realtimeActivity, setRealtimeActivity] = useState([]);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const activityIdRef = useRef(10000);

  // ─── Derived live data ────────────────────────────────────────────────────
  const heatmapData = useMemo(() => buildHeatmapData(stays, rooms, selectedBuildings), [stays, rooms, selectedBuildings]);
  // Always compute metrics on ALL buildings (not filtered) for KPI cards
  const allBuildingsHeatmap = useMemo(() => buildHeatmapData(stays, rooms, []), [stays, rooms]);
  const { totalRooms, occupiedRooms, reservedRooms, availableRooms, occupancyRate, pendingCheckins, activeResidents, upcomingDepartures, alertBuildings } =
    useMemo(() => computeMetrics(stays, rooms, allBuildingsHeatmap), [stays, rooms, allBuildingsHeatmap]);
  const contextActivityFeed = useMemo(() => buildActivityFeed(stays), [stays]);
  const trendData = useMemo(() => buildTrendData(stays), [stays]);

  // Diagnostic for H-101 and H-001 (validation report)
  const validationReport = useMemo(() => generateValidationReport(['H-101', 'H-001'], stays, rooms), [stays, rooms]);
  const h101Diagnostic = useMemo(() => diagnoseRoom('H-101', realtime?.stays || []), [realtime?.stays]);

  // Merge context activity + realtime activity, deduplicate, sort newest first
  const mergedActivity = [...realtimeActivity, ...contextActivityFeed]
    ?.filter((item, idx, arr) => arr?.findIndex(x => x?.id === item?.id) === idx)
    ?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    ?.slice(0, 15);

  // ─── Capacity alerts from live data ──────────────────────────────────────
  useEffect(() => {
    const newAlerts = alertBuildings?.map(b => {
      const total = b?.floors?.reduce((s, f) => s + f?.total, 0);
      const occ = b?.floors?.reduce((s, f) => s + f?.occupied, 0);
      const pct = total > 0 ? Math.round((occ / total) * 100) : 0;
      return {
        id: `cap-${b?.building}`,
        severity: pct >= 95 ? 'critical' : 'warning',
        title: pct >= 95 ? 'Critical Capacity Alert' : 'High Occupancy Warning',
        message: `${b?.building} has reached ${pct}% occupancy. ${total - occ} room(s) remaining.`,
        action: 'View Details',
      };
    });
    setAlerts(newAlerts);
  }, [stays, selectedBuildings]);

  // ─── Supabase real-time subscription on residents table ──────────────────
  useEffect(() => {
    const channel = supabase?.channel('occupancy-analytics-realtime')?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'residents' },
        (payload) => {
          setIsSyncing(true);
          setLastSyncTime(new Date());

          const record = payload?.new || payload?.old;
          const eventType = payload?.eventType;

          if (record) {
            const isCheckout = eventType === 'DELETE' || record?.is_active === false;
            const activityEntry = {
              id: activityIdRef.current++,
              type: isCheckout ? 'check-out' : 'check-in',
              title: isCheckout ? 'Check-out (DB Sync)' : eventType === 'UPDATE' ? 'Record Updated' : 'New Check-in (DB Sync)',
              description: isCheckout
                ? `${record?.full_name || 'Resident'} checked out from Room ${record?.room_number || ''}`
                : `${record?.full_name || 'Resident'} assigned to Room ${record?.room_number || ''} in ${record?.building || ''}`,
              location: `${record?.building || ''} · Room ${record?.room_number || ''}`,
              timestamp: new Date(),
            };
            setRealtimeActivity(prev => [activityEntry, ...prev]?.slice(0, 20));
          }

          setTimeout(() => setIsSyncing(false), 1500);
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  // ─── Update sync time whenever stays change ───────────────────────────────
  const prevStaysLenRef = useRef(stays?.length);
  useEffect(() => {
    if (stays?.length !== prevStaysLenRef?.current) {
      setIsSyncing(true);
      setLastSyncTime(new Date());
      prevStaysLenRef.current = stays?.length;
      setTimeout(() => setIsSyncing(false), 1200);
    }
  }, [stays]);

  // ─── Metrics cards ────────────────────────────────────────────────────────
  const metricsData = [
    {
      title: t?.occupancy?.currentOccupancyRate || 'Current Occupancy Rate',
      value: `${occupancyRate}%`,
      subtitle: `${occupiedRooms} of ${totalRooms} rooms`,
      iconName: 'Users',
      trend: 'up',
      trendValue: `${occupiedRooms} occupied rooms`,
      status: parseFloat(occupancyRate) >= 90 ? 'danger' : parseFloat(occupancyRate) >= 75 ? 'warning' : 'success',
    },
    {
      title: t?.occupancy?.availableRooms || 'Available Rooms',
      value: String(availableRooms),
      subtitle: `${reservedRooms} reserved · ${totalRooms} total`,
      iconName: 'Home',
      trend: availableRooms < 50 ? 'down' : 'neutral',
      trendValue: `${reservedRooms} rooms reserved`,
      status: availableRooms < 20 ? 'danger' : availableRooms < 50 ? 'warning' : 'neutral',
    },
    {
      title: t?.occupancy?.pendingCheckins || 'Pending Check-ins',
      value: String(pendingCheckins),
      subtitle: t?.occupancy?.expectedToday || 'Expected today',
      iconName: 'LogIn',
      trend: 'neutral',
      trendValue: t?.occupancy?.onSchedule || 'On schedule',
      status: 'neutral',
    },
    {
      title: t?.occupancy?.capacityAlerts || 'Capacity Alerts',
      value: String(alertBuildings?.length),
      subtitle: t?.occupancy?.buildingsAbove90 || 'Buildings above 90%',
      iconName: 'AlertTriangle',
      trend: alertBuildings?.length > 0 ? 'up' : 'neutral',
      trendValue: alertBuildings?.length > 0 ? alertBuildings?.map(b => b?.building)?.join(', ') : 'All clear',
      status: alertBuildings?.length > 0 ? 'danger' : 'success',
    },
  ];

  // ─── Refresh: fetch live data from Supabase ───────────────────────────────
  const handleRefresh = useCallback(async () => {
    setIsSyncing(true);
    try {
      await realtime?.fetchStays?.();
      await realtime?.fetchResidents?.();
    } catch (e) {
      console.error('Refresh error:', e);
    }
    setLastSyncTime(new Date());
    setIsSyncing(false);
  }, [realtime]);

  const handleRoomClick = (room) => {
    console.log('Room clicked:', room);
  };

  const handleDismissAlert = (alertId) => {
    setAlerts(prev => prev?.filter(a => a?.id !== alertId));
  };

  const handleResetFilters = () => {
    setSelectedBuildings([]);
    setSelectedRoomTypes([]);
    setDateRange('month');
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
              {t?.occupancy?.title || 'Occupancy Analytics'}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              {language === 'fr' ?'Surveillance en temps réel et analyses prédictives' :'Real-time monitoring and predictive insights'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Diagnostic toggle */}
            <button
              onClick={() => setShowDiagnostic(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              {showDiagnostic ? 'Hide Diagnostic' : 'Room Diagnostic'}
            </button>
            {/* Live sync badge */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              isSyncing
                ? 'bg-amber-100 text-amber-700 border border-amber-200' :'bg-success/10 text-success border border-success/20'
            }`}>
              <div className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-amber-500 animate-pulse' : 'bg-success animate-pulse'}`} />
              {isSyncing
                ? (language === 'fr' ? 'Synchronisation…' : 'Syncing…')
                : (language === 'fr' ? `En direct · ${lastSyncTime?.toLocaleTimeString()}` : `Live · ${lastSyncTime?.toLocaleTimeString()}`)}
            </div>
          </div>
        </div>

        {/* ─── Diagnostic Panel ─────────────────────────────────────────── */}
        {showDiagnostic && (
          <div className="bg-card border border-amber-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Occupancy Validation Report — Date-Driven Logic
              </h3>
              <button onClick={() => setShowDiagnostic(false)} className="text-muted-foreground hover:text-foreground text-xs">✕ Close</button>
            </div>

            {/* Validation Report Table */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Room Validation Report (H-101 &amp; H-001)</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      {['Room', 'Arrival Date', 'Departure Date', 'Check-In Status', 'Current Status', 'Corrected Status', 'Root Cause', 'Fix Applied']?.map(h => (
                        <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium border-b border-border whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {validationReport?.map((row, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="px-3 py-2 font-mono font-bold text-foreground">{row?.roomNumber}</td>
                        <td className="px-3 py-2 text-muted-foreground">{row?.arrivalDate}</td>
                        <td className="px-3 py-2 text-muted-foreground">{row?.departureDate}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${
                            row?.checkInStatus === 'confirmed-stay' ? 'bg-emerald-100 text-emerald-700' :
                            row?.checkInStatus === 'active-reservation'? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'
                          }`}>{row?.checkInStatus}</span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{row?.currentStatus}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full font-semibold ${
                            row?.correctedStatus === 'Occupied'    ? 'bg-rose-100 text-rose-700' :
                            row?.correctedStatus === 'Reserved'   ? 'bg-amber-100 text-amber-700' :
                            row?.correctedStatus === 'Maintenance'? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'
                          }`}>{row?.correctedStatus}</span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground max-w-[200px]">{row?.rootCause}</td>
                        <td className="px-3 py-2 text-emerald-700 max-w-[200px]">{row?.fixApplied}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Room Number', value: h101Diagnostic?.roomNumber, color: 'text-foreground' },
                { label: 'Stay Records Found', value: String(h101Diagnostic?.stayCount), color: h101Diagnostic?.stayCount > 0 ? 'text-emerald-600' : 'text-rose-600' },
                { label: 'Is Occupied (Date-Driven)', value: h101Diagnostic?.isOccupied ? '✅ Yes' : '❌ No', color: h101Diagnostic?.isOccupied ? 'text-emerald-600' : 'text-rose-600' },
                { label: 'Is Reserved', value: h101Diagnostic?.isReserved ? '⚠️ Yes' : 'No', color: h101Diagnostic?.isReserved ? 'text-amber-600' : 'text-muted-foreground' },
              ]?.map((item, i) => (
                <div key={i} className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">{item?.label}</p>
                  <p className={`text-sm font-semibold ${item?.color}`}>{item?.value}</p>
                </div>
              ))}
            </div>

            {/* Root cause */}
            <div className={`rounded-lg p-4 border ${
              h101Diagnostic?.isOccupied
                ? 'bg-emerald-50 border-emerald-200'
                : h101Diagnostic?.isReserved
                  ? 'bg-amber-50 border-amber-200' :'bg-rose-50 border-rose-200'
            }`}>
              <p className="text-xs font-bold uppercase tracking-wider mb-1 text-muted-foreground">Root Cause / Status</p>
              <p className="text-sm text-foreground">{h101Diagnostic?.rootCause}</p>
            </div>

            {/* Stay records */}
            {h101Diagnostic?.stays?.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Stay Records for H-101</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        {['Stay ID', 'Status', 'Guest Status', 'Resident', 'Check-in', 'Check-out', 'Room Field']?.map(h => (
                          <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium border-b border-border">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {h101Diagnostic?.stays?.map((s, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="px-3 py-2 font-mono text-muted-foreground">{s?.id?.slice(0, 8)}…</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${
                              s?.status === 'confirmed-stay' ? 'bg-emerald-100 text-emerald-700' :
                              s?.status === 'active-reservation' ? 'bg-amber-100 text-amber-700' :
                              s?.status === 'completed-stay'? 'bg-slate-100 text-slate-600' : 'bg-muted text-muted-foreground'
                            }`}>{s?.status}</span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{s?.guestStatus || '—'}</td>
                          <td className="px-3 py-2 text-foreground">{s?.residentName}</td>
                          <td className="px-3 py-2 text-muted-foreground">{s?.checkIn}</td>
                          <td className="px-3 py-2 text-muted-foreground">{s?.checkOut}</td>
                          <td className="px-3 py-2 font-mono text-foreground font-semibold">{s?.roomField}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Fix instructions */}
            {!h101Diagnostic?.isOccupied && h101Diagnostic?.isReserved && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Fix Required</p>
                <p className="text-sm text-blue-800">
                  Room H-101 has an <strong>active-reservation</strong> but has not been checked in yet.
                  Go to <strong>Check-in / Check-out → Check-in tab</strong> and register the resident's arrival to convert the status to <strong>confirmed-stay</strong>.
                  Once checked in, the heatmap will immediately show the room as <span className="font-bold text-rose-600">Occupied (Red)</span> and occupancy metrics will update.
                </p>
              </div>
            )}

            {/* All stays summary */}
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
              {[
                { label: 'Total Stays in DB', value: realtime?.stays?.length || 0 },
                { label: 'Confirmed (Occupied)', value: realtime?.stays?.filter(s => s?.status === 'confirmed-stay')?.length || 0 },
                { label: 'Active Reservations', value: realtime?.stays?.filter(s => s?.status === 'active-reservation')?.length || 0 },
              ]?.map((item, i) => (
                <div key={i} className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-foreground">{item?.value}</p>
                  <p className="text-xs text-muted-foreground">{item?.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <RefreshControl
          onRefresh={handleRefresh}
          autoRefreshInterval={autoRefreshInterval}
          onIntervalChange={setAutoRefreshInterval}
        />

        <CapacityAlert alerts={alerts} onDismiss={handleDismissAlert} />

        {/* Metric cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* Heatmap + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          <div className="lg:col-span-8">
            <OccupancyHeatmap data={heatmapData} onRoomClick={handleRoomClick} />
          </div>
          <div className="lg:col-span-4">
            <ActivityFeed activities={mergedActivity} />
          </div>
        </div>

        {/* Trend charts + Filter */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          <div className="lg:col-span-8 space-y-6">
            <OccupancyTrendChart data={trendData} />
          </div>
          <div className="lg:col-span-4">
            <FilterPanel
              buildings={ALL_BUILDINGS}
              selectedBuildings={selectedBuildings}
              onBuildingsChange={setSelectedBuildings}
              roomTypes={ROOM_TYPES}
              selectedRoomTypes={selectedRoomTypes}
              onRoomTypesChange={setSelectedRoomTypes}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              onReset={handleResetFilters}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OccupancyAnalytics;